import * as React from "react";
import { Luggage, Plus, ChevronRight, Check, Trash2, Package as PackageIcon, Briefcase, X } from "lucide-react";
import { apiFetch } from "@/lib/api.js";
import { useToast } from "@/components/Toast.js";
import { cn } from "@/lib/utils.js";
import { Modal } from "@/components/Modal.js";
import { ConfirmDialog } from "@/components/ConfirmDialog.js";

interface PackingListSummary {
  id: string; name: string; description: string | null; status: string; totalItems: number; packedItems: number; createdAt: string; updatedAt: string;
}
interface PackingBag { id: string; name: string; packingListId: string; }
interface PackingListItem {
  id: string; itemId: string | null; customName: string | null; bagId: string | null; packed: boolean; itemName?: string; itemSpaceName?: string;
}
interface PackingList extends PackingListSummary { bags: PackingBag[]; items: PackingListItem[]; }
interface InventoryItem { id: string; name: string; spaceName: string | null; }

export default function PackingListsPage() {
  const [lists, setLists] = React.useState<PackingListSummary[]>([]);
  const [activeList, setActiveList] = React.useState<PackingList | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [showCreate, setShowCreate] = React.useState(false);
  const [showAddItem, setShowAddItem] = React.useState(false);
  const [showAddBag, setShowAddBag] = React.useState(false);
  const [deleteId, setDeleteId] = React.useState<string | null>(null);
  const [inventoryItems, setInventoryItems] = React.useState<InventoryItem[]>([]);
  const toast = useToast();

  const loadLists = React.useCallback(async () => {
    try { setLists(await apiFetch<PackingListSummary[]>("/api/packing-lists")); } catch {} finally { setLoading(false); }
  }, []);

  const loadList = async (id: string) => {
    try { setActiveList(await apiFetch<PackingList>(`/api/packing-lists/${id}`)); } catch (e: any) { toast.show(e.message, "error"); }
  };

  React.useEffect(() => { loadLists(); }, [loadLists]);

  // Create list
  const [newName, setNewName] = React.useState("");
  const [newDesc, setNewDesc] = React.useState("");
  const createList = async () => {
    if (!newName.trim()) return;
    try {
      const list = await apiFetch<PackingList>("/api/packing-lists", { method: "POST", body: JSON.stringify({ name: newName, description: newDesc || null }) });
      setLists((prev) => [{ ...list, totalItems: 0, packedItems: 0 }, ...prev]);
      setActiveList(list);
      setShowCreate(false); setNewName(""); setNewDesc("");
      toast.show("Packing list created");
    } catch (e: any) { toast.show(e.message, "error"); }
  };

  // Delete list
  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await apiFetch(`/api/packing-lists/${deleteId}`, { method: "DELETE" });
      setLists((prev) => prev.filter((l) => l.id !== deleteId));
      if (activeList?.id === deleteId) setActiveList(null);
      setDeleteId(null);
      toast.show("Packing list deleted");
    } catch (e: any) { toast.show(e.message, "error"); }
  };

  // Add bag
  const [bagName, setBagName] = React.useState("");
  const addBag = async () => {
    if (!bagName.trim() || !activeList) return;
    try {
      const bag = await apiFetch<PackingBag>(`/api/packing-lists/${activeList.id}/bags`, { method: "POST", body: JSON.stringify({ name: bagName }) });
      setActiveList((prev) => prev ? { ...prev, bags: [...prev.bags, bag] } : prev);
      setBagName(""); setShowAddBag(false);
    } catch (e: any) { toast.show(e.message, "error"); }
  };

  // Delete bag
  const deleteBag = async (bagId: string) => {
    if (!activeList) return;
    try {
      await apiFetch(`/api/packing-lists/${activeList.id}/bags/${bagId}`, { method: "DELETE" });
      setActiveList((prev) => prev ? { ...prev, bags: prev.bags.filter((b) => b.id !== bagId), items: prev.items.map((i) => i.bagId === bagId ? { ...i, bagId: null } : i) } : prev);
    } catch (e: any) { toast.show(e.message, "error"); }
  };

  // Add item
  const [customItemName, setCustomItemName] = React.useState("");
  const [selectedItemId, setSelectedItemId] = React.useState<string | null>(null);
  const [selectedBagId, setSelectedBagId] = React.useState<string | null>(null);
  const [itemSearch, setItemSearch] = React.useState("");

  const openAddItem = async () => {
    setShowAddItem(true);
    setItemSearch("");
    setInventoryItems([]);
  };

  React.useEffect(() => {
    if (!showAddItem || !itemSearch.trim()) { if (showAddItem && !itemSearch.trim()) setInventoryItems([]); return; }
    const t = setTimeout(async () => {
      try { setInventoryItems(await apiFetch<InventoryItem[]>(`/api/items?search=${encodeURIComponent(itemSearch)}&limit=20`)); } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [itemSearch, showAddItem]);

  const addItem = async () => {
    if (!activeList || (!selectedItemId && !customItemName.trim())) return;
    try {
      const item = await apiFetch<PackingListItem>(`/api/packing-lists/${activeList.id}/items`, {
        method: "POST", body: JSON.stringify({ itemId: selectedItemId, customName: customItemName || null, bagId: selectedBagId }),
      });
      // Enrich with name
      const inv = inventoryItems.find((i) => i.id === selectedItemId);
      const enriched: PackingListItem = { ...item, itemName: inv?.name, itemSpaceName: inv?.spaceName ?? undefined };
      setActiveList((prev) => prev ? { ...prev, items: [...prev.items, enriched] } : prev);
      setShowAddItem(false); setCustomItemName(""); setSelectedItemId(null); setSelectedBagId(null); setItemSearch("");
    } catch (e: any) { toast.show(e.message, "error"); }
  };

  // Toggle packed
  const togglePacked = async (plItem: PackingListItem) => {
    if (!activeList) return;
    try {
      await apiFetch(`/api/packing-lists/${activeList.id}/items/${plItem.id}`, { method: "PUT", body: JSON.stringify({ packed: !plItem.packed }) });
      setActiveList((prev) => prev ? { ...prev, items: prev.items.map((i) => i.id === plItem.id ? { ...i, packed: !i.packed } : i) } : prev);
    } catch (e: any) { toast.show(e.message, "error"); }
  };

  // Assign to bag
  const assignBag = async (plItemId: string, bagId: string | null) => {
    if (!activeList) return;
    try {
      await apiFetch(`/api/packing-lists/${activeList.id}/items/${plItemId}`, { method: "PUT", body: JSON.stringify({ bagId }) });
      setActiveList((prev) => prev ? { ...prev, items: prev.items.map((i) => i.id === plItemId ? { ...i, bagId } : i) } : prev);
    } catch (e: any) { toast.show(e.message, "error"); }
  };

  // Remove item
  const removeItem = async (plItemId: string) => {
    if (!activeList) return;
    try {
      await apiFetch(`/api/packing-lists/${activeList.id}/items/${plItemId}`, { method: "DELETE" });
      setActiveList((prev) => prev ? { ...prev, items: prev.items.filter((i) => i.id !== plItemId) } : prev);
    } catch (e: any) { toast.show(e.message, "error"); }
  };

  const filteredInventory = inventoryItems.filter((i) => !itemSearch || i.name.toLowerCase().includes(itemSearch.toLowerCase()));

  const modalInput = "w-full px-3 py-2.5 rounded-sm border border-clay-border text-sm text-body placeholder:text-faint focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30";

  if (loading) return <div className="space-y-4">{[1,2,3].map((i) => <div key={i} className="h-20 bg-clay-muted rounded-lg animate-pulse" />)}</div>;

  // Detail view
  if (activeList) {
    const packed = activeList.items.filter((i) => i.packed).length;
    const total = activeList.items.length;
    const pct = total > 0 ? Math.round((packed / total) * 100) : 0;
    const unassigned = activeList.items.filter((i) => !i.bagId);

    return (
      <div className="space-y-6">
        <header>
          <button onClick={() => { setActiveList(null); loadLists(); }} className="text-sm text-primary hover:text-amethyst-ink mb-2 inline-block">← Back to lists</button>
          <h1 className="text-2xl font-semibold text-ink font-headline tracking-[-0.01em]">{activeList.name}</h1>
          {activeList.description && <p className="text-body mt-1 text-sm">{activeList.description}</p>}
          <div className="flex items-center gap-3 mt-3">
            <div className="flex-1 h-2 bg-clay-muted rounded-pill overflow-hidden"><div className="h-full bg-primary rounded-pill transition-all" style={{ width: `${pct}%` }} /></div>
            <span className="text-sm font-medium text-body tabular-nums">{packed}/{total} packed</span>
          </div>
        </header>

        <div className="flex gap-2">
          <button onClick={openAddItem} className="flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors"><Plus className="w-4 h-4" />Add Item</button>
          <button onClick={() => setShowAddBag(true)} className="flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-amethyst-soft text-amethyst-ink text-sm font-medium hover:bg-clay-muted transition-colors"><Briefcase className="w-4 h-4" />Add Bag</button>
        </div>

        {/* Bags */}
        {activeList.bags.map((bag) => {
          const bagItems = activeList.items.filter((i) => i.bagId === bag.id);
          return (
            <section key={bag.id} className="bg-white rounded-lg border border-clay-border p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2"><Briefcase className="w-4 h-4 text-sage-deep" /><h3 className="text-[1.0625rem] font-semibold text-ink">{bag.name}</h3><span className="text-[0.8125rem] text-faint tabular-nums">{bagItems.length} items</span></div>
                <button onClick={() => deleteBag(bag.id)} className="p-1 rounded-sm hover:bg-error-wash transition-colors" aria-label={`Delete bag ${bag.name}`}><Trash2 className="w-3.5 h-3.5 text-error" /></button>
              </div>
              {bagItems.length === 0 ? <p className="text-[0.8125rem] text-faint pl-6">No items in this bag yet — assign from below.</p> : (
                <div className="space-y-1">{bagItems.map((item) => <ItemRow key={item.id} item={item} bags={activeList.bags} onToggle={togglePacked} onAssign={assignBag} onRemove={removeItem} />)}</div>
              )}
            </section>
          );
        })}

        {/* Unassigned */}
        {unassigned.length > 0 && (
          <section className="bg-white rounded-lg border border-clay-border p-4">
            <h3 className="text-[1.0625rem] font-semibold text-ink mb-3">Unassigned ({unassigned.length})</h3>
            <div className="space-y-1">{unassigned.map((item) => <ItemRow key={item.id} item={item} bags={activeList.bags} onToggle={togglePacked} onAssign={assignBag} onRemove={removeItem} />)}</div>
          </section>
        )}

        {total === 0 && (
          <div className="bg-sage-wash rounded-lg border border-clay-border p-12 text-center">
            <PackageIcon className="w-12 h-12 text-sage-deep mx-auto mb-3" />
            <p className="text-[1.0625rem] font-semibold text-ink mb-1">Nothing to pack yet</p>
            <p className="text-sm text-body">Add items from your inventory or create custom ones.</p>
          </div>
        )}

        {/* Add Item Modal */}
        <Modal open={showAddItem} onClose={() => setShowAddItem(false)} title="Add Item to Pack" wide>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-body mb-1 block">From Inventory</label>
              <input value={itemSearch} onChange={(e) => setItemSearch(e.target.value)} placeholder="Search items…" className={modalInput} />
              {itemSearch && (
                <div className="max-h-40 overflow-y-auto mt-1 border border-clay-border rounded-md">
                  {filteredInventory.slice(0, 20).map((inv) => (
                    <button key={inv.id} onClick={() => { setSelectedItemId(inv.id); setCustomItemName(""); setItemSearch(inv.name); }}
                      className={cn("w-full text-left px-3 py-2 text-sm text-body hover:bg-paper transition-colors", selectedItemId === inv.id && "bg-amethyst-soft text-amethyst-ink")}>
                      {inv.name} {inv.spaceName && <span className="text-faint">· {inv.spaceName}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="text-center text-[0.8125rem] text-faint">— or —</div>
            <div>
              <label className="text-sm font-medium text-body mb-1 block">Custom Item</label>
              <input value={customItemName} onChange={(e) => { setCustomItemName(e.target.value); setSelectedItemId(null); }} placeholder="e.g. Toothbrush" className={modalInput} />
            </div>
            {activeList.bags.length > 0 && (
              <div>
                <label className="text-sm font-medium text-body mb-1 block">Assign to Bag</label>
                <select value={selectedBagId ?? ""} onChange={(e) => setSelectedBagId(e.target.value || null)} className={modalInput}>
                  <option value="">Unassigned</option>
                  {activeList.bags.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            )}
            <button onClick={addItem} disabled={!selectedItemId && !customItemName.trim()} className="w-full py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed">Add to List</button>
          </div>
        </Modal>

        {/* Add Bag Modal */}
        <Modal open={showAddBag} onClose={() => setShowAddBag(false)} title="Add Bag">
          <div className="space-y-4">
            <input value={bagName} onChange={(e) => setBagName(e.target.value)} placeholder="e.g. Carry-on, Backpack, Suitcase" className={modalInput} />
            <button onClick={addBag} disabled={!bagName.trim()} className="w-full py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed">Add Bag</button>
          </div>
        </Modal>
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-8">
      <header>
        <div className="flex items-center gap-2 mb-1"><Luggage className="w-5 h-5 text-primary" /><span className="text-sm font-medium text-primary">Packing</span></div>
        <h1 className="text-2xl font-semibold text-ink font-headline tracking-[-0.01em]">Packing Lists</h1>
        <p className="text-body mt-1 text-sm">Organize items into bags for trips — items keep their permanent home</p>
      </header>

      <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors"><Plus className="w-4 h-4" />New Packing List</button>

      {lists.length === 0 ? (
        <div className="bg-sage-wash rounded-lg border border-clay-border p-12 text-center">
          <Luggage className="w-12 h-12 text-sage-deep mx-auto mb-3" />
          <p className="text-[1.0625rem] font-semibold text-ink">No packing lists yet</p>
          <p className="text-sm text-body mt-1">Create one for your next trip.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {lists.map((list) => {
            const pct = list.totalItems > 0 ? Math.round((list.packedItems / list.totalItems) * 100) : 0;
            const completed = list.status === "completed";
            return (
              <button key={list.id} onClick={() => loadList(list.id)} className="w-full bg-white rounded-lg border border-clay-border p-4 text-left hover:border-amethyst/40 transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[1.0625rem] font-semibold text-ink">{list.name}</p>
                    <p className="text-[0.8125rem] text-faint mt-0.5"><span className="tabular-nums">{list.totalItems}</span> items · <span className="tabular-nums">{list.packedItems}</span> packed</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={cn("text-[0.8125rem] font-medium px-2 py-0.5 rounded-pill", completed ? "bg-success-wash text-success-text" : "bg-amethyst-soft text-amethyst-ink")}>{list.status}</span>
                    <button onClick={(e) => { e.stopPropagation(); setDeleteId(list.id); }} className="p-1 rounded-sm hover:bg-error-wash transition-colors" aria-label={`Delete ${list.name}`}><Trash2 className="w-4 h-4 text-error" /></button>
                    <ChevronRight className="w-4 h-4 text-faint" />
                  </div>
                </div>
                {list.totalItems > 0 && (
                  <div className="mt-2 h-1.5 bg-clay-muted rounded-pill overflow-hidden"><div className="h-full bg-primary rounded-pill" style={{ width: `${pct}%` }} /></div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Create Modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="New Packing List">
        <div className="space-y-4">
          <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Beach Vacation, Business Trip" className={modalInput} />
          <textarea value={newDesc} onChange={(e) => setNewDesc(e.target.value)} placeholder="Description (optional)" rows={2} className={modalInput + " resize-none"} />
          <button onClick={createList} disabled={!newName.trim()} className="w-full py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed">Create List</button>
        </div>
      </Modal>

      <ConfirmDialog open={deleteId !== null} onCancel={() => setDeleteId(null)} onConfirm={confirmDelete} title="Delete packing list?" message="This will permanently delete this packing list and all its items." destructive />
    </div>
  );
}

function ItemRow({ item, bags, onToggle, onAssign, onRemove }: {
  item: PackingListItem; bags: PackingBag[];
  onToggle: (item: PackingListItem) => void;
  onAssign: (id: string, bagId: string | null) => void;
  onRemove: (id: string) => void;
}) {
  const name = item.itemName ?? item.customName ?? "Unknown";
  return (
    <div className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-paper transition-colors">
      <button onClick={() => onToggle(item)} className={cn("w-5 h-5 rounded-sm border-2 flex items-center justify-center shrink-0 transition-colors",
        item.packed ? "bg-primary border-primary" : "border-clay-border")} role="checkbox" aria-checked={item.packed} aria-label={item.packed ? `Mark ${name} unpacked` : `Mark ${name} packed`}>
        {item.packed && <Check className="w-3 h-3 text-white" />}
      </button>
      <span className={cn("text-sm flex-1", item.packed ? "line-through text-faint" : "text-body")}>{name}</span>
      {item.itemSpaceName && <span className="text-[0.8125rem] text-faint">{item.itemSpaceName}</span>}
      <select value={item.bagId ?? ""} onChange={(e) => onAssign(item.id, e.target.value || null)}
        className="text-[0.8125rem] text-faint bg-transparent border-none outline-none cursor-pointer" aria-label={`Assign ${name} to a bag`}>
        <option value="">No bag</option>
        {bags.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
      </select>
      <button onClick={() => onRemove(item.id)} className="p-1 rounded-sm hover:bg-error-wash transition-colors" aria-label={`Remove ${name}`}><X className="w-3 h-3 text-error" /></button>
    </div>
  );
}
